import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DeleteJson, GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { FormInput, FormSelect, FormTextArea } from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { formatJson, isIntStr, isJsonString, isValueEmpty } from '@awp/helpers/utilsReact';
import SensorAssociatedModelTable from '@awp/components/SensorModelAdmin/SensorAssociatedModelTable';
import { ObservationDataPrecedence } from '@components/Common/ObservationDataPrecedence';
import { getObservationData } from '@graphs/Presets/Helpers/PresetHelper';
import { selectAllManufacturers, selectAllModelstatuses, selectAllTypes } from '@stores/sensorModelSlice';
import isEqual from 'lodash/isEqual';
import SensorModelService from '@components/SensorModelAdmin/SensorModelService';

const SensorModelEdit = ({ setMode, mode, sensormodelId, data, setData, setLoading, loading }) => {
  const initPrecedence = {
    ObservationTypeId: null,
    Relative_Precedence_Number: '0.5',
    SensorTypeId: null,
    ModelSpecificationId: null,
  };
  const allModelstatuses = useSelector(selectAllModelstatuses);
  const allTypes = useSelector(selectAllTypes);
  const allManufacturers = useSelector(selectAllManufacturers);

  const [sensorModelData, setSensorModelData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sensorData, setSensorData] = useState([]);
  const [searchSensor, setSearchSensor] = useState('');
  const [filteredSensorData, setFilteredSensorData] = useState([]);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [observationData, setObservationData] = useState([]);
  const [observations, setObservations] = useState([initPrecedence]);
  const [precedenceErrorMessage, setPrecedenceErrorMessage] = useState('');
  const [initialDto, setInitialDto] = useState({});

  const crudService = new SensorModelService(mode);
  const { title, readOnly, successMessage, buttonLabel, entityName } = crudService.crud;

  const tableSensorProps = {
    sensormodelData: sensorModelData,
    sensorData,
    sensorLoading,
    setMode,
    searchSensor,
    setSearchSensor,
    filteredSensorData,
    setFilteredSensorData,
  };

  useEffect(() => {
    fetchData();
    if (mode === FORM_MODE.CREATE) {
      setSensorModelData({ Name: '', Definition: '' });
      setInitialDto(
        crudService.mapInitialValues({
          entity: {
            ...sensorModelData,
            Precedences: observations,
          },
          getDefaultStatus,
        }),
      );
      return;
    }
    if (mode === FORM_MODE.VIEW) {
      setSensorLoading(true);
      const url = REST_API.SENSOR_EQUIPMENT_ASSOCIATE_TO_MODEL + sensormodelId;
      GetJson(url)
        .then((response) => response.json())
        .then((data) => {
          setSensorData(data);
          setFilteredSensorData(data);
          setSensorLoading(false);
        });
    }
    setLoading(true);
    const url = REST_API.SENSOR_MODEL + sensormodelId;
    GetJson(url)
      .then((response) => response.json())
      .then((sm) => {
        setSensorModelData(sm);
        sm.Precedences = sm.Precedences != null && sm.Precedences.length > 0 ? [...sm.Precedences] : [initPrecedence];
        setObservations(sm.Precedences);

        setInitialDto(crudService.mapInitialValues({ entity: sm, getDefaultStatus }));
        setLoading(false);
      });
  }, []);

  const fetchData = async () => {
    try {
      let data = [];
      data = await getObservationData();
      setObservationData(data);
    } catch (error) {
      console.error(`Unable to fetchData for ${entityName} dropdown`);
    }
  };

  const convertDropdownOptions = (options) => {
    if (isValueEmpty(options) || options.length === 0) return [];
    let newOptions = [];
    options.map((t) => {
      newOptions.push({ value: t.Id, label: t.Name });
    });
    return newOptions;
  };
  const updateSensorModel = (setSubmitting, resetForm, setErrors, sensormodelDto) => {
    const url = REST_API.SENSOR_MODEL + sensormodelDto.Id;

    PutJson(url, sensormodelDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const sm = await response.json();
          sm.Precedences = sm.Precedences.length == 0 ? [...sm.Precedences, ...[initPrecedence]] : sm.Precedences;
          setObservations(sm.Precedences);
          setSensorModelData(sm);
          updateData(sm);

          const updatedDto = crudService.mapInitialValues({ entity: sm, getDefaultStatus });
          setInitialDto(updatedDto);
          resetForm({
            values: updatedDto,
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ SensorModel: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createSensorModel = (setSubmitting, resetForm, setErrors, sensormodelDto) => {
    const url = REST_API.SENSOR_MODEL;

    PostJson(url, sensormodelDto)
      .then(async (response) => {
        if (response.ok) {
          const sm = await response.json();
          sm.Precedences = sm.Precedences.length == 0 ? [...sm.Precedences, ...[initPrecedence]] : sm.Precedences;
          setObservations(sm.Precedences);
          setSensorModelData(sm);
          updateData(sm);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);

          const updatedDto = crudService.mapInitialValues({ entity: sm, getDefaultStatus });
          setInitialDto(updatedDto);
          resetForm({
            values: updatedDto,
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ SensorModel: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteSensorModel = () => {
    const id = sensorModelData.Id;
    const url = REST_API.SENSOR_MODEL + id;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          setSensorModelData({});
          removeData(id);
          toast.success(`${entityName} has been deleted`);
          setMode(FORM_MODE.LIST);
        } else if (response.status === 412) {
          const errors = await response.json();
          //setErrors(errors);
          if (errors.entity) setEntityErrors({ SensorModel: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    setPrecedenceErrorMessage('');

    const sensormodelDto = crudService.mapFormToDTO({
      entity: {
        ...sensorModelData,
      },
      values,
      observations,
    });

    if (mode === FORM_MODE.EDIT) {
      updateSensorModel(setSubmitting, resetForm, setErrors, sensormodelDto);
    } else {
      createSensorModel(setSubmitting, resetForm, setErrors, sensormodelDto);
    }
  };

  const updateData = (ot) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === ot.Id);
      if (index < 0) return;
      data[index] = ot;
      setData([...data]);
    } else {
      setData([...data, ot]);
    }
  };
  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };
  const getDefaultStatus = () => {
    if (!allModelstatuses || allModelstatuses.length === 0) return {};
    let s = allModelstatuses.find((ws) => ws.Name == 'Active');
    return { value: s.Id, label: s.Name };
  };

  const handleAddObservation = () => {
    setObservations([...observations, ...[initPrecedence]]);
  };

  const handleDeleteObservation = (index) => {
    if (observations.length == 1 && observations[0].ObservationTypeId == null) {
      return;
    }
    const newObservations = observations.filter((_, idx) => idx !== index);
    newObservations.length == 0 ? setObservations([initPrecedence]) : setObservations(newObservations);
  };

  const handleObservationTypeChange = (index, newTypeId) => {
    const updatedObservations = observations.map((obs, idx) =>
      idx === index
        ? {
            ...obs,
            ObservationTypeId: newTypeId,
            ModelSpecificationId: sensorModelData.Id,
          }
        : obs,
    );
    setObservations(updatedObservations);
  };

  const handlePrecedenceChange = (index, newPrecedence) => {
    const updatedObservations = observations.map((obs, idx) =>
      idx === index
        ? {
            ...obs,
            Relative_Precedence_Number: isValueEmpty(newPrecedence) ? 0.5 : parseFloat(newPrecedence),
            ModelSpecificationId: sensorModelData.Id,
          }
        : obs,
    );
    setObservations(updatedObservations);
  };

  const isDisabled = (props) => {
    let isManufactureIdEmpty =
      isValueEmpty(props.values?.manufacturerid?.value) || props.values?.manufacturerid?.value == 0;
    let isTypeIdEmpty = isValueEmpty(props.values?.typeid?.value) || props.values?.typeid?.value == 0;
    let isModelNumberEmpty = isValueEmpty(props.values?.modelnumber) || props.values?.modelnumber?.length == 0;
    let isStatusTypeIdEmpty = isValueEmpty(props.values?.statustypeid?.value) || props.values?.statustypeid?.value == 0;
    return isManufactureIdEmpty || isTypeIdEmpty || isModelNumberEmpty || isStatusTypeIdEmpty;
  };

  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={crudService.mapInitialValues({
              entity: {
                ...sensorModelData,
              },
              getDefaultStatus,
            })}
            validateOnMount={true}
            initialTouched={{
              manufacturerid: true,
              modelnumber: true,
              lowerlimit: true,
              upperlimit: true,
              sensorMetadata: true,
              typeid: true,
            }}
            validationSchema={Yup.object({
              modelnumber: Yup.string()
                .max(50, 'Maximum 50 characters are allowed')
                .min(2, 'Minimum 2 characters are required')
                .required('Model is a required field'),
              lowerlimit: Yup.string()
                .max(10, 'Maximum 10 characters are allowed')
                .test('checkmodlowerlimit', function () {
                  if (!isValueEmpty(this.parent.lowerlimit) && !isIntStr(this.parent.lowerlimit)) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'lowerlimit',
                    });
                  }
                  if (
                    !isValueEmpty(this.parent.lowerlimit) &&
                    !isValueEmpty(this.parent.upperlimit) &&
                    Number(this.parent.lowerlimit) > Number(this.parent.upperlimit)
                  ) {
                    return this.createError({
                      message: 'Lower limit should be less than upper limit',
                      path: 'lowerlimit',
                    });
                  }
                  return true;
                }),
              upperlimit: Yup.string()
                .max(10, 'Maximum 10 characters are allowed')
                .test('checkmodupperlimit', function () {
                  if (!isValueEmpty(this.parent.upperlimit) && !isIntStr(this.parent.upperlimit)) {
                    return this.createError({
                      message: 'Please enter a valid number',
                      path: 'upperlimit',
                    });
                  }
                  if (
                    !isValueEmpty(this.parent.lowerlimit) &&
                    !isValueEmpty(this.parent.upperlimit) &&
                    Number(this.parent.lowerlimit) > Number(this.parent.upperlimit)
                  ) {
                    return this.createError({
                      message: 'Upper limit should be greater than lower limit',
                      path: 'upperlimit',
                    });
                  }
                  return true;
                }),
              sensorMetadata: Yup.string()
                .max(2000, 'Maximum 2000 characters are allowed')
                .test('isJson', 'Metadata must be a valid JSON', (value) => {
                  if (!value) return true;

                  if (value.trim() === '') return true;

                  return isJsonString(value);
                }),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => {
              const { PersistentPrecedentFilters, ...propsValues } = props.values;
              const myIsPristine = isEqual(propsValues, initialDto) && isEqual(initialDto.Precedences, observations);

              return (
                <Form>
                  <span className="awp-text-btn" onClick={() => setMode('list')}>
                    List Sensor Models
                  </span>
                  <span className="mx-3">&gt;</span>
                  <span>{title}</span>
                  <Card border="light" className="page-card">
                    <Card.Header>
                      <Row className="mt-4"></Row>
                      {crudService.crud.getHeader()}
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={4} className="mb-4">
                          <FormSelect
                            readOnly={readOnly}
                            label="Manufacturer (required)"
                            id="manufacturerid"
                            name="manufacturerid"
                            textValue={sensorModelData.ManufacturerName ?? ''}
                            options={convertDropdownOptions(allManufacturers)}
                            isMulti={false}
                          />
                        </Col>
                        <Col sm={4} className="mb-4">
                          <FormInput
                            label="Model (required)"
                            instruction=""
                            readOnly={readOnly || sensorModelData.IsProtected === 'Y'}
                            name="modelnumber"
                            id="modelnumber"
                          />
                        </Col>
                        <Col sm={4} className="mb-4">
                          <FormSelect
                            readOnly={readOnly}
                            label="Type (required)"
                            id="typeid"
                            name="typeid"
                            textValue={sensorModelData.Type ?? ''}
                            options={convertDropdownOptions(allTypes)}
                            isMulti={false}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={4} className="mb-4">
                          <FormInput
                            label="Lower limit"
                            instruction=""
                            readOnly={readOnly || sensorModelData.IsProtected === 'Y'}
                            name="lowerlimit"
                            id="lowerlimit"
                          />
                        </Col>
                        <Col sm={4} className="mb-4">
                          <FormInput
                            label="Upper limit"
                            instruction=""
                            readOnly={readOnly || sensorModelData.IsProtected === 'Y'}
                            name="upperlimit"
                            id="upperlimit"
                          />
                        </Col>
                        <Col sm={4} className="mb-4">
                          <FormSelect
                            readOnly={readOnly}
                            label="Status (required)"
                            id="statustypeid"
                            name="statustypeid"
                            textValue={sensorModelData.Status ?? ''}
                            options={convertDropdownOptions(allModelstatuses)}
                            isMulti={false}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={8} className="mb-4">
                          <FormTextArea
                            readOnly={readOnly}
                            label="Sensor Equipment Metadata"
                            instruction="(2000 Characters max)"
                            id="sensorMetadata"
                            name="sensorMetadata"
                            rows={3}
                            onBlur={(e) => {
                              props.setFieldValue('sensorMetadata', formatJson(e.target.value));
                              props.setFieldTouched('sensorMetadata');
                            }}
                          />
                        </Col>
                      </Row>
                      <ObservationDataPrecedence
                        observationData={observationData}
                        errorMessage={precedenceErrorMessage}
                        setErrorMessage={setPrecedenceErrorMessage}
                        persistentPrecedentFilters={observations}
                        onAdd={handleAddObservation}
                        onDelete={(index) => {
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].ObservationTypeId`, null);
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].Relative_Precedence_Number`, '0.5');

                          props.setFieldValue(`PersistentPrecedentFilters[${index}].SensorTypeId`, null);
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].ModelSpecificationId`, null);

                          handleDeleteObservation(index);

                          props.setFieldTouched(`PersistentPrecedentFilters[${index}].ObservationTypeId`, true, false);
                        }}
                        onObservationTypeChange={(index, id) => {
                          props.setFieldValue(`PersistentPrecedentFilters[${index}].ObservationTypeId`, id);
                          props.setFieldTouched(`PersistentPrecedentFilters[${index}].ObservationTypeId`, true, false);
                          handleObservationTypeChange(index, id);
                        }}
                        onPrecedenceChange={(index, id) => {
                          handlePrecedenceChange(index, id);
                        }}
                        readOnly={mode !== FORM_MODE.VIEW}
                      />
                    </Card.Body>
                    <Card.Footer>
                      {!showDeleteConfirm ? (
                        <Row className="my-2">
                          <AwpFormActionButtons
                            title=" "
                            entityName={entityName}
                            buttonLabel={buttonLabel}
                            mode={mode}
                            setMode={setMode}
                            onClickDelete={onClickDelete}
                            permissions={[Permissions.WriteSensorModel]}
                            readOnly={readOnly}
                            disableSave={isDisabled(props)}
                            isAlignLeft={true}
                            btnCancelName={'Close'}
                            props={props}
                            isPristine={myIsPristine}
                          />
                        </Row>
                      ) : (
                        <Row className="my-2">
                          <AwpYesNoDialog
                            action={deleteSensorModel}
                            setShowYesNoConfirm={setShowDeleteConfirm}
                            props={props}
                          />
                        </Row>
                      )}
                    </Card.Footer>
                  </Card>
                </Form>
              );
            }}
          </Formik>
          {mode === FORM_MODE.VIEW && (
            <Row>
              <Col>{sensorLoading ? <AwpSpinner /> : <SensorAssociatedModelTable {...tableSensorProps} />}</Col>
            </Row>
          )}
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default SensorModelEdit;
